// src/Pages/Landing/Startups/StartupLandingPage.jsx
//
// Landing page for founders and early-stage teams.
// Same structure and design tokens as BusinessLandingPage; copy written for
// the founder's problem: social media is the first thing to slip, and the
// usual fixes (freelancer, scheduler, ChatGPT) all still need the founder.
//
// CLAIMS: every capability here is live today — owner announcements, news
// signals, scheduled posts to Facebook/Instagram/LinkedIn, email approval,
// per-platform timing, learning from edits. Social posting ONLY on this page —
// no profile, chatbot, FAQ, blog or discovery claims. No X/Twitter (mock only),
// no integrations (GitHub, changelog, Product Hunt) — do not add them here
// until they exist.

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import EnhancedSEO from '../../../Components/SEO/EnhancedSEO';
import {
  Box, Container, Grid, Typography, Button, Chip, Stack, Divider,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  ArrowRightAlt as ArrowFlowIcon,
  CheckCircle as CheckIcon,
  RocketLaunch as LaunchIcon,
  Newspaper as NewsIcon,
  MarkEmailRead as InboxIcon,
  CalendarMonth as CadenceIcon,
  Schedule as TimingIcon,
  TrendingUp as LearnIcon,
  PersonSearch as FreelancerIcon,
  EventNote as SchedulerIcon,
  SmartToy as GenericAIIcon,
  Bolt as BoltIcon,
  Edit as DescribeIcon,
  Campaign as TellIcon,
  TaskAlt as ApproveIcon,
} from '@mui/icons-material';

// ─── Design tokens (same as BusinessLandingPage) ───────────────────────────────

const BRAND = {
  navy:    '#0D2B6B',
  blue:    '#1976D2',
  lightBg: '#F3F9FF',
  slate:   '#455A7B',
  muted:   '#90A4AE',
  border:  '#E2E8F0',
};

// ─── Signal → outcome, startup edition ─────────────────────────────────────────

const SIGNAL_FLOW = [
  {
    Icon:      LaunchIcon,
    signal:    'You type one line: "Hindi support is live"',
    signalAt:  '2:14pm',
    outcome:   'Launch post drafted for LinkedIn, Facebook and Instagram',
    outcomeAt: '2:16pm',
    tint:      '#81D4FA',
  },
  {
    Icon:      NewsIcon,
    signal:    'A news story breaks in your space',
    signalAt:  'Tuesday',
    outcome:   'A post connecting it to what you build',
    outcomeAt: 'Wed 9am',
    tint:      '#A5D6A7',
  },
  {
    Icon:      InboxIcon,
    signal:    'Both drafts land in your inbox',
    signalAt:  'Wed 8am',
    outcome:   'One tap to approve, posted at the best hour, your time',
    outcomeAt: 'Done',
    tint:      '#CE93D8',
  },
];

// ─── The usual fixes vs Revfer ─────────────────────────────────────────────────

const ALTERNATIVES = [
  {
    Icon:     FreelancerIcon,
    category: 'A freelancer',
    verdict:  'Someone you have to brief',
    body:     'They do not know your product, so every week starts with you explaining it again. Then you review, correct and chase — and when they move on, you start over.',
    points:   ['You write the brief every week', 'Learning your product takes months', 'A monthly retainer either way'],
    isRevfer: false,
  },
  {
    Icon:     SchedulerIcon,
    category: 'A scheduling tool',
    verdict:  'A calendar you still fill',
    body:     'It will post at 9am on Tuesday. It will not decide what to say. The empty queue is still yours to fill.',
    points:   ['You write every post', 'You decide every topic', 'Silence when you get busy'],
    isRevfer: false,
  },
  {
    Icon:     GenericAIIcon,
    category: 'ChatGPT and friends',
    verdict:  'A blank box',
    body:     'Fast and cheap, and it forgets your product the moment you close the tab. You still prompt, paste context, edit, copy and post.',
    points:   ['You start it every time', 'No memory of your product', 'You still do the posting'],
    isRevfer: false,
  },
  {
    Icon:     BoltIcon,
    category: 'Revfer',
    verdict:  'A marketer that already knows your product',
    body:     'Revfer knows what you build and who it is for. It notices what is happening and drafts the post. You read it and tap approve.',
    points:   ['Starts the work itself', 'Knows your product and your voice', 'Publishes on approval'],
    isRevfer: true,
  },
];

// ─── Capabilities ──────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    Icon:     LaunchIcon,
    title:    'Launches and updates',
    headline: 'Shipped something? One line is enough.',
    body:     'Tell Revfer what happened — a feature, a milestone, a new customer, a hire — in a sentence, in any language. It writes a post for each platform in your voice and queues it for your approval.',
    color:    '#0277BD',
    bg:       'rgba(2,119,189,0.07)',
  },
  {
    Icon:     NewsIcon,
    title:    'News in your space',
    headline: 'Something happened in your industry. You have a take.',
    body:     'Revfer watches the news around what you build and drafts posts that connect it to your product — the kind of timely post founders mean to write and never get to.',
    color:    '#2E7D32',
    bg:       'rgba(46,125,50,0.07)',
  },
  {
    Icon:     CadenceIcon,
    title:    'A steady cadence',
    headline: 'Your feed stays alive in your busiest weeks.',
    body:     'Revfer picks the schedule and keeps it: 1 post a week on the free plan, 3 a week on Premium, across LinkedIn, Facebook and Instagram. Launch week or fundraising week, the feed does not go quiet.',
    color:    '#E65100',
    bg:       'rgba(230,81,0,0.07)',
  },
  {
    Icon:     InboxIcon,
    title:    'Approve from your inbox',
    headline: 'Thirty seconds between meetings.',
    body:     'Each draft arrives by email. Approve it with one tap, or open it in Revfer to edit first. Nothing is published until you say so.',
    color:    '#1565C0',
    bg:       'rgba(21,101,192,0.07)',
  },
  {
    Icon:     TimingIcon,
    title:    'Posted at the right time',
    headline: 'The best hour for each platform, in your time zone.',
    body:     'Revfer schedules every approved post for the hour that platform works best, where you are — and keeps LinkedIn to weekdays. No calendar for you to manage.',
    color:    '#6A1B9A',
    bg:       'rgba(106,27,154,0.07)',
  },
  {
    Icon:     LearnIcon,
    title:    'Learns your voice',
    headline: 'Every edit teaches it.',
    body:     'Every draft you reword, skip or approve untouched feeds back in. Month three sounds more like you than week one — without a single brief.',
    color:    '#0D2B6B',
    bg:       'rgba(13,43,107,0.07)',
  },
];

// FAQ — shown on the page AND used for the FAQPage schema (Google requires
// structured data to match visible content). Mirrored in seeker renderstatic.js
// (STARTUP_FAQ) for bots: keep the two identical.
// Written to be quotable as-is: each answer stands alone, so search engines
// and AI assistants can cite one without the page around it.
const STARTUP_FAQ = [
  ["What does Revfer do for startups?",
   "Revfer keeps a startup's social media active without the founder writing posts. It learns what the product does and who it is for, then drafts posts for LinkedIn, Facebook and Instagram from launches the founder mentions and news in the startup's space. The founder approves each draft and Revfer publishes it on a schedule it chooses."],
  ["How is Revfer different from hiring a social media freelancer?",
   "A freelancer needs a brief every week and takes months to learn the product. Revfer is described once, drafts posts on its own from what is happening, and learns the founder's voice from every edit. The founder's job is reviewing and approving, not briefing, and the plan costs $19 a month at launch pricing."],
  ["How is Revfer different from Buffer, Hootsuite or ChatGPT?",
   "Scheduling tools post what you write; general AI writes when you prompt it. Both still depend on the founder starting the work. Revfer starts the work itself: it decides when a post is due, drafts it from the product facts and current signals, and publishes on approval."],
  ["Which platforms does Revfer post to?",
   "LinkedIn, Facebook and Instagram. Each approved post is scheduled for that platform's best hour in the founder's time zone."],
  ["Do I need to write prompts?",
   "No. You describe your product once. After that you can mention news in one line — a launch, a milestone, a hire — or say nothing, and Revfer keeps a regular cadence from your product facts and industry news."],
  ["How much does Revfer cost?",
   "There is a free plan with one post a week. Premium is $19 a month at launch pricing ($49 after launch) with three posts a week and up to five products or brands. No credit card is needed to start."],
];

const HOW_IT_WORKS = [
  {
    Icon:  DescribeIcon,
    step:  '01',
    title: 'Describe your product once',
    body:  'What it does, who it is for, what is new. A few specific lines. Revfer only writes about what you tell it, so specific in means specific out.',
    time:  '10 min',
  },
  {
    Icon:  TellIcon,
    step:  '02',
    title: 'Mention what happens — or do nothing',
    body:  'Shipped a feature? Type one line. Busy week? Revfer keeps the cadence going from your product facts and the news in your space.',
    time:  'When you want',
  },
  {
    Icon:  ApproveIcon,
    step:  '03',
    title: 'Approve from your phone',
    body:  'Read the draft, tap approve. It publishes to LinkedIn, Facebook or Instagram at the time Revfer picked. Edit if you like — that teaches it.',
    time:  '30 sec',
  },
  {
    Icon:  LearnIcon,
    step:  '04',
    title: 'It gets more like you',
    body:  'Revfer keeps a history of what you approved, changed and skipped, and what your audience engaged with. The drafts sharpen every month.',
    time:  'Ongoing',
  },
];

// ─── Hero signal strip ─────────────────────────────────────────────────────────

const HeroSignalStrip = () => (
  <Box sx={{ bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 4, p: { xs: 3, md: 3.5 }, border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}>
    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 2, fontSize: 10, fontWeight: 700, display: 'block', mb: 2.5 }}>
      WHAT A WEEK LOOKS LIKE
    </Typography>

    {SIGNAL_FLOW.map(({ Icon, signal, signalAt, outcome, outcomeAt, tint }, i, arr) => (
      <Box
        key={signal}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 2,
          pb: i < arr.length - 1 ? 2.25 : 0,
          mb: i < arr.length - 1 ? 2.25 : 0,
          borderBottom: i < arr.length - 1 ? '1px dashed rgba(255,255,255,0.14)' : 'none',
        }}
      >
        <Box sx={{ width: 38, height: 38, borderRadius: 1.75, bgcolor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon sx={{ fontSize: 19, color: tint }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, lineHeight: 1.4, display: 'block' }}>
            {signal}
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.35)' }}>{` · ${signalAt}`}</Box>
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 600, fontSize: 13.5, lineHeight: 1.45, mt: 0.4, display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
            <ArrowFlowIcon sx={{ color: tint, fontSize: 17, mt: '-1px', flexShrink: 0 }} />
            {outcome}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0, px: 1.25, py: 0.4, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.1)' }}>
          <Typography variant="caption" sx={{ color: tint, fontWeight: 700, fontSize: 10.5, whiteSpace: 'nowrap' }}>
            {outcomeAt}
          </Typography>
        </Box>
      </Box>
    ))}

    <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, lineHeight: 1.55 }}>
        You wrote one line. Everything else was drafted for you.
      </Typography>
    </Box>
  </Box>
);

// ─── Alternative card ──────────────────────────────────────────────────────────

const AlternativeCard = ({ item }) => {
  const { Icon, category, verdict, body, points, isRevfer } = item;
  return (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      p: { xs: 3, md: 3.5 }, borderRadius: 3,
      bgcolor: isRevfer ? BRAND.navy : 'white',
      border:  isRevfer ? `2px solid ${BRAND.blue}` : `1px solid ${BRAND.border}`,
      boxShadow: isRevfer ? '0 12px 40px rgba(13,43,107,0.22)' : 'none',
      transition: 'all 0.25s ease',
      '&:hover': isRevfer ? { transform: 'translateY(-4px)' } : { borderColor: '#CBD5E1' },
    }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2, mb: 2, bgcolor: isRevfer ? 'rgba(129,212,250,0.16)' : '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon sx={{ fontSize: 22, color: isRevfer ? '#81D4FA' : BRAND.muted }} />
      </Box>
      <Typography variant="overline" sx={{ letterSpacing: 1.5, fontSize: 10, fontWeight: 700, display: 'block', mb: 0.75, color: isRevfer ? 'rgba(255,255,255,0.55)' : BRAND.muted }}>
        {category}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, mb: 1.5, fontSize: { xs: 18, md: 19 }, color: isRevfer ? '#81D4FA' : '#64748B' }}>
        {verdict}
      </Typography>
      <Typography variant="body2" sx={{ lineHeight: 1.75, fontSize: 13.5, mb: 2.5, flex: 1, color: isRevfer ? 'rgba(255,255,255,0.8)' : BRAND.slate }}>
        {body}
      </Typography>
      <Box>
        {points.map(p => (
          <Box key={p} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1, '&:last-child': { mb: 0 } }}>
            {isRevfer
              ? <CheckIcon sx={{ color: '#81D4FA', fontSize: 15, mt: 0.25, flexShrink: 0 }} />
              : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#CBD5E1', mt: '7px', flexShrink: 0 }} />}
            <Typography variant="body2" sx={{ fontSize: 12.5, lineHeight: 1.5, color: isRevfer ? 'rgba(255,255,255,0.85)' : '#7B8FA8' }}>
              {p}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Capability card ───────────────────────────────────────────────────────────

const CapabilityCard = ({ cap }) => (
  <Box sx={{
    bgcolor: 'white', borderRadius: 3, p: { xs: 3, md: 3.5 }, height: '100%',
    border: `1px solid ${BRAND.border}`, transition: 'all 0.25s ease',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 10px 28px ${cap.color}18`, borderColor: `${cap.color}40` },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: cap.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <cap.Icon sx={{ color: cap.color, fontSize: 23 }} />
      </Box>
      <Typography variant="overline" sx={{ color: cap.color, letterSpacing: 1.5, fontSize: 10, fontWeight: 700 }}>
        {cap.title}
      </Typography>
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.navy, lineHeight: 1.3, fontSize: { xs: 16, md: 17 }, mb: 1 }}>
      {cap.headline}
    </Typography>
    <Typography variant="body2" sx={{ color: BRAND.slate, lineHeight: 1.7, fontSize: 13.5 }}>
      {cap.body}
    </Typography>
  </Box>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const StartupLandingPage = () => {
  const navigate       = useNavigate();
  const { authStatus } = useAuthenticator((c) => [c.authStatus]);

  const handleCTA = () => {
    if (authStatus === 'authenticated') {
      navigate('/company/new');
    } else {
      navigate('/auth/signin', { state: { name: 'business', link: '/landing/startups' } });
    }
  };

  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": STARTUP_FAQ.map(([q, a]) => ({
      "@type": "Question", "name": q, "acceptedAnswer": { "@type": "Answer", "text": a },
    })),
  };


  return (
    <>
      <EnhancedSEO
        title="Revfer for Startups — social media that keeps itself alive"
        description="Revfer keeps your startup's LinkedIn, Facebook and Instagram active without a freelancer. Describe your product once; Revfer drafts posts from your launches and news in your space. You approve, it publishes. Free to start, $19/mo at launch."
        keywords="social media for startups, startup social media automation, founder LinkedIn posts, alternative to social media freelancer, AI social media manager for startups, startup marketing without a marketer"
        type="website"
        image="/og-image.jpg"
        canonical="https://www.revfer.com/landing/startups"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Revfer for Startups', url: '/landing/startups' }]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* ═══ HERO ════════════════════════════════════════════════════════════ */}
      <Box component="section" sx={{
        background: `linear-gradient(150deg, ${BRAND.navy} 0%, #1A3A7A 60%, #1565C0 100%)`,
        color: 'white', pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 10 },
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04, backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="For founders and early-stage teams"
                size="small"
                sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: 12, border: '1px solid rgba(255,255,255,0.2)' }}
              />
              <Typography component="h1" variant="h1" sx={{ fontSize: { xs: '2rem', sm: '2.6rem', md: '3rem' }, fontWeight: 800, lineHeight: 1.15, mb: 3 }}>
                Your startup's social media,<br />
                kept alive.<br />
                <Box component="span" sx={{ background: 'linear-gradient(90deg, #81D4FA, #A5F3FC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Without hiring for it.
                </Box>
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.85, lineHeight: 1.7, mb: 4, fontWeight: 400, fontSize: { xs: '1rem', md: '1.1rem' }, maxWidth: 540 }}>
                You know you should post every week. Then a release slips, a customer needs you,
                and the feed goes quiet for a month. Revfer knows your product, notices what is
                happening, and drafts the posts — you just approve them.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 4 }}>
                <Button
                  variant="contained" size="large" endIcon={<ArrowIcon />} onClick={handleCTA}
                  sx={{ bgcolor: 'white', color: BRAND.blue, fontWeight: 700, fontSize: 16, px: 4, py: 2, borderRadius: 3, textTransform: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.92)', transform: 'translateY(-2px)' } }}
                >
                  {authStatus === 'authenticated' ? 'Set Up My Startup' : 'Start Free — 15 min setup'}
                </Button>
                <Typography variant="body2" sx={{ opacity: 0.65, fontSize: 13, textAlign: { xs: 'center', sm: 'left' } }}>
                  No credit card · No brief to write · Cancel anytime
                </Typography>
              </Stack>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {[
                  { value: '3 posts/wk', label: 'on Premium, 1/wk free'   },
                  { value: '$19/mo',     label: 'launch pricing'          },
                  { value: '0 briefs',   label: 'describe your product once' },
                ].map(({ value, label }) => (
                  <Box key={label}>
                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1, mb: 0.25 }}>{value}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 500 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <HeroSignalStrip />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══ THE PROBLEM ═════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: '#FFFBF0', py: { xs: 6, md: 8 }, borderBottom: '1px solid #FFE082' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#5D4037', textAlign: 'center', mb: 1.5, fontSize: { xs: '1.3rem', md: '1.6rem' } }}>
            Your last LinkedIn post was six weeks ago. It always is.
          </Typography>
          <Typography variant="body1" sx={{ color: '#6D4C41', textAlign: 'center', maxWidth: 620, mx: 'auto', lineHeight: 1.7 }}>
            Not because you do not care. Because the product, the customers and the next
            release always come first — and posting is the first thing to slip. So you look
            for a freelancer, spend weeks explaining the product, and end up rewriting their
            drafts anyway.{' '}
            <Box component="strong">The feed still goes quiet when you get busy.</Box>
          </Typography>
        </Container>
      </Box>

      {/* ═══ THE USUAL FIXES ═════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: BRAND.lightBg }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Chip label="The usual fixes" size="small" sx={{ mb: 2, bgcolor: 'rgba(25,118,210,0.1)', color: BRAND.blue, fontWeight: 700, border: '1px solid rgba(25,118,210,0.2)' }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2 }}>
              Every fix you have tried<br />still waits for you to start it.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 600, mx: 'auto', lineHeight: 1.75 }}>
              The hard part of startup social media is not the writing. It is knowing the product
              well enough to say something real — and starting, every single week.
            </Typography>
          </Box>
          <Grid container spacing={3} alignItems="stretch">
            {ALTERNATIVES.map(item => (
              <Grid key={item.category} item xs={12} sm={6} md={3}>
                <AlternativeCard item={item} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══ CAPABILITIES ════════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Chip label="What Revfer does" size="small" sx={{ mb: 2, bgcolor: 'rgba(25,118,210,0.1)', color: BRAND.blue, fontWeight: 700, border: '1px solid rgba(25,118,210,0.2)' }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              The marketer you would hire,<br />if you had time to brief one.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 560, mx: 'auto', lineHeight: 1.75 }}>
              Everything below is live today, on LinkedIn, Facebook and Instagram.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {CAPABILITIES.map(cap => (
              <Grid key={cap.title} item xs={12} sm={6} md={4}>
                <CapabilityCard cap={cap} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══ HOW IT WORKS ════════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: BRAND.lightBg }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
            <Chip label="Setup" size="small" sx={{ mb: 2, bgcolor: 'rgba(46,125,50,0.1)', color: '#2E7D32', fontWeight: 700, border: '1px solid rgba(46,125,50,0.2)' }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 1.5, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              Describe it once.<br />Then get back to building.
            </Typography>
          </Box>
          <Stack spacing={3}>
            {HOW_IT_WORKS.map(({ Icon, step, title, body, time }, i) => (
              <Box key={step} sx={{
                display: 'flex', gap: { xs: 2.5, md: 4 }, alignItems: 'flex-start',
                p: { xs: 3, md: 4 }, bgcolor: 'white', borderRadius: 3, border: `1px solid ${BRAND.border}`,
                transition: 'all 0.2s', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.06)', borderColor: '#BBDEFB' },
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: BRAND.lightBg, border: '1px solid #BBDEFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ color: BRAND.blue, fontSize: 22 }} />
                  </Box>
                  {i < HOW_IT_WORKS.length - 1 && <Box sx={{ width: 2, height: 24, bgcolor: '#BBDEFB', borderRadius: 1 }} />}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="overline" sx={{ color: BRAND.muted, letterSpacing: 2, fontSize: 10, fontWeight: 700 }}>STEP {step}</Typography>
                    <Chip label={`⏱ ${time}`} size="small" sx={{ bgcolor: 'rgba(46,125,50,0.08)', color: '#2E7D32', fontWeight: 700, fontSize: 11, height: 22 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.navy, mb: 0.75, fontSize: { xs: 16, md: 18 } }}>{title}</Typography>
                  <Typography variant="body2" sx={{ color: BRAND.slate, lineHeight: 1.7, fontSize: 14 }}>{body}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ═══ FOUNDER NOTE ════════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 9 }, bgcolor: 'white' }}>
        <Container maxWidth="md">
          <Box sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, border: `1px solid ${BRAND.border}`, bgcolor: BRAND.lightBg }}>
            <Typography variant="overline" sx={{ color: BRAND.blue, letterSpacing: 1.8, fontSize: 10, fontWeight: 700, display: 'block', mb: 1.5 }}>
              BUILT BY A FOUNDER WITH THE SAME PROBLEM
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: BRAND.navy, mb: 2, fontSize: { xs: '1.25rem', md: '1.5rem' }, lineHeight: 1.35 }}>
              Revfer's own LinkedIn, Facebook and Instagram are run by Revfer.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, lineHeight: 1.8, fontSize: 15 }}>
              While building Revfer, our own social media kept going quiet — every week the product
              won and the post lost. Freelancers needed the product explained again and again.
              So Revfer runs Revfer's accounts now: it drafts from what we ship and what is happening
              in our space, and we approve from our phones. That is the product you are signing up for.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ═══ PRICING ═════════════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: BRAND.lightBg }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 1.5, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              Less than one freelancer hour a month.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 460, mx: 'auto', lineHeight: 1.7 }}>
              Start free. Upgrade when you want three posts a week.
            </Typography>
          </Box>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 4, border: `1px solid ${BRAND.border}`, borderRadius: 3, height: '100%', bgcolor: 'white' }}>
                <Typography variant="overline" sx={{ color: BRAND.muted, letterSpacing: 2, fontSize: 11, fontWeight: 700, display: 'block', mb: 2 }}>FREE</Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, color: BRAND.navy, mb: 3 }}>
                  $0<Typography component="span" variant="body1" sx={{ color: BRAND.muted, fontWeight: 500 }}>/mo</Typography>
                </Typography>
                {[
                  '1 post per week',
                  'LinkedIn, Facebook and Instagram',
                  'Launch and update posts from one line',
                  'Approve by email in one tap',
                  '1 product or brand',
                ].map(f => (
                  <Box key={f} sx={{ display: 'flex', gap: 1.5, mb: 1.25, alignItems: 'flex-start' }}>
                    <CheckIcon sx={{ color: '#81C784', fontSize: 17, mt: 0.2, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: BRAND.slate, fontSize: 14 }}>{f}</Typography>
                  </Box>
                ))}
                <Button fullWidth variant="outlined" size="large" onClick={handleCTA}
                  sx={{ mt: 3, borderColor: BRAND.border, color: BRAND.slate, borderRadius: 2.5, textTransform: 'none', fontWeight: 600 }}>
                  Start Free
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 4, bgcolor: BRAND.navy, borderRadius: 3, height: '100%', position: 'relative', overflow: 'hidden' }}>
                <Chip label="Launch pricing" size="small" sx={{ position: 'absolute', top: 20, right: 20, bgcolor: '#FF6F00', color: 'white', fontWeight: 700, fontSize: 11 }} />
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 2, fontSize: 11, fontWeight: 700, display: 'block', mb: 2 }}>PREMIUM</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: 'white', lineHeight: 1 }}>
                    $19<Typography component="span" variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>/mo</Typography>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through', fontWeight: 500 }}>$49/mo</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#FFB74D', fontWeight: 700, display: 'block', mb: 3 }}>Regular price $49/mo after launch</Typography>
                {[
                  '3 posts per week',
                  'LinkedIn, Facebook and Instagram',
                  'Posts from news in your space',
                  'Approve by email in one tap',
                  'Up to 5 products or brands',
                ].map(f => (
                  <Box key={f} sx={{ display: 'flex', gap: 1.5, mb: 1.25, alignItems: 'flex-start' }}>
                    <CheckIcon sx={{ color: '#81D4FA', fontSize: 17, mt: 0.2, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>{f}</Typography>
                  </Box>
                ))}
                <Button fullWidth variant="contained" size="large" onClick={handleCTA} endIcon={<ArrowIcon />}
                  sx={{ mt: 3, bgcolor: BRAND.blue, color: 'white', borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontSize: 16, py: 1.75, '&:hover': { bgcolor: '#1565C0' } }}>
                  Start Free — Upgrade Anytime
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══ FAQ (matches the FAQPage schema) ═════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 9 }, bgcolor: 'white' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 4, textAlign: 'center', fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            Frequently asked questions
          </Typography>
          <Stack spacing={3}>
            {STARTUP_FAQ.map(([q, a]) => (
              <Box key={q}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: BRAND.navy, mb: 0.75, fontSize: { xs: 16, md: 18 } }}>
                  {q}
                </Typography>
                <Typography variant="body2" sx={{ color: BRAND.slate, lineHeight: 1.75, fontSize: 14.5 }}>
                  {a}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ═══ BOTTOM CTA ══════════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 8, md: 10 }, background: `linear-gradient(150deg, ${BRAND.navy} 0%, #1565C0 100%)`, color: 'white', textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2 }}>
            Ship the product.<br />
            <Box component="span" sx={{ color: '#81D4FA' }}>Revfer keeps the feed alive.</Box>
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.75, mb: 5, lineHeight: 1.7 }}>
            For founders who know consistency matters — and know they will not be the ones keeping it up.
          </Typography>
          <Button variant="contained" size="large" endIcon={<ArrowIcon />} onClick={handleCTA}
            sx={{ bgcolor: 'white', color: BRAND.blue, fontWeight: 700, fontSize: 17, px: 5, py: 2.25, borderRadius: 3, textTransform: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.92)', transform: 'translateY(-2px)' } }}>
            {authStatus === 'authenticated' ? 'Set Up My Startup' : 'Start Free — 15 min setup'}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.5 }}>
            No credit card required · Free plan · Cancel anytime
          </Typography>
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.15)' }} />
          <Button variant="text" size="small" onClick={() => navigate('/landing/business')}
            sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontWeight: 600, '&:hover': { color: 'white' } }}>
            Run a local business instead? See Revfer for restaurants, salons and services →
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default StartupLandingPage;